import api from "./client"
import type { Routine } from "../types"

export async function getRoutines(
  params: { date?: string; from?: string; to?: string; userId?: number } = {}
): Promise<Routine[]> {
  const query: Record<string, unknown> = {}
  if (params.date) query.date = params.date
  if (params.from) query.from = params.from
  if (params.to) query.to = params.to
  if (params.userId) query.user_id = params.userId
  const { data } = await api.get<Routine[]>("/routines", { params: query })
  return data
}

// Recurrencia: la rutina se repite en `weekdays` (0=domingo … 6=sábado) durante
// `weeks` semanas, a partir de `start_date`. El backend crea una fila por ocurrencia
// con un series_id compartido y devuelve el arreglo de rutinas creadas.
export interface RoutineRecurrence {
  start_date: string // YYYY-MM-DD
  weekdays: number[]
  weeks: number
}

export async function createRoutineSeries(
  params: Record<string, unknown>,
  recurrence: RoutineRecurrence,
  userId?: number
): Promise<Routine[]> {
  const body: Record<string, unknown> = { routine: { ...params, recurrence } }
  if (userId) body.user_id = userId
  const { data } = await api.post<Routine[]>("/routines", body)
  return data
}

export async function getRoutine(id: number): Promise<Routine> {
  const { data } = await api.get<Routine>(`/routines/${id}`)
  return data
}

export async function createRoutine(
  params: Record<string, unknown>,
  userId?: number
): Promise<Routine> {
  const body: Record<string, unknown> = { routine: params }
  if (userId) body.user_id = userId
  const { data } = await api.post<Routine>("/routines", body)
  return data
}

// scope "series" propaga los campos escalares (nombre, descripción, horario) a
// todas las rutinas de la serie; "single" (default) edita solo esta ocurrencia.
export async function updateRoutine(
  id: number,
  params: Record<string, unknown>,
  userId?: number,
  scope: "single" | "series" = "single"
): Promise<Routine> {
  const body: Record<string, unknown> = { routine: params }
  if (userId) body.user_id = userId
  if (scope === "series") body.scope = "series"
  const { data } = await api.patch<Routine>(`/routines/${id}`, body)
  return data
}

// Resultado de propagar la edición de una plantilla a las rutinas asignadas.
export interface PropagationResult {
  routines: number
  clients: number
}

// Edita una plantilla de la galería y propaga el cambio a las rutinas ya asignadas
// que salieron de ella (de hoy en adelante). Se propagan nombre, descripción y el
// alta/baja de ejercicios; el peso de cada cliente NO se toca.
export async function updateTemplateAndPropagate(
  id: number,
  params: Record<string, unknown>
): Promise<{ routine: Routine; propagated: PropagationResult }> {
  const { data } = await api.patch<Routine & { propagated?: PropagationResult }>(
    `/routines/${id}`,
    { routine: params, propagate: "upcoming" }
  )
  const { propagated, ...routine } = data
  return { routine, propagated: propagated ?? { routines: 0, clients: 0 } }
}

// scope "series" borra todas las rutinas de la serie; "single" (default) solo esta.
export async function deleteRoutine(
  id: number,
  userId?: number,
  scope: "single" | "series" = "single"
): Promise<void> {
  const params: Record<string, unknown> = {}
  if (userId) params.user_id = userId
  if (scope === "series") params.scope = "series"
  await api.delete(`/routines/${id}`, { params })
}

export async function getTemplates(): Promise<Routine[]> {
  const { data } = await api.get<Routine[]>("/routines/templates")
  return data
}
